import {
  CycleWithRels,
  PlayerGameWithRels,
  Gender,
  Mapping,
  Player,
  PlayerWithRels,
  TeamWithRels,
  PlayerNavigationLinkIds,
  MetricDistributionPercentiles,
  MetricDistributionBucket,
} from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithQuery, atomWithSuspenseQuery } from 'jotai-tanstack-query';
import localforage from 'localforage';
import { filtersAtom } from '@/atoms/filters/filter';
import { fetchClientAtom } from '@/atoms/queries/client';
import { metricDistributionPercentilesUrl, metricDistributionBucketsUrl } from '@/query/url';
import { StringOption } from '@/types/generic';

import { isSharingId } from '@/utils/array';
import { createCompetitionOptionsAtom, createSeasonOptionsAtom } from '@/utils/atoms/cycles';
import { sortCyclesBySeasonAndComp } from '@/utils/cycles';
import { formatYearInfo } from '@/utils/date';
import { filterCyclesBy, filterCyclesWithRelsByCompetition } from '@/utils/filters';
import { datahubToIqIdMapping } from '@/utils/idMapping';
import {
  formatMetricDistributionBuckets,
  getMetricDistribution,
  getMetricBucket,
  formatMetricDistributions,
} from '@/utils/metrics';
import { convertToDropdownOption } from '@/utils/object';
import { buildOptionSearchIndexAsync } from '@/utils/optionSearch';
import { formatPlayerName } from '@/utils/player';
import { objSnakeToCamel } from '@/utils/queries';
import { competitionIdsToFilterByAtom } from '../filters/highLevel/competitions';
import { seasonIdsToFilterByAtom } from '../filters/highLevel/seasons';
import { metricsAtom } from '../metric';
import { globalPlayerIdAtom, hasUserSelectedPlayerAtom, playerIdAtom } from '../player/player';
import { radarTemplatePositionGroupAtom, selectedPlayerTemplatePositionGroupAtom } from '../radar';
import { rawCyclesAtom } from './cycles';
import { unwrappedGenderAtom } from './userConfigs';

const rawPlayersPath = (gender: Gender | undefined) => `/players?eq[gender]=${gender}`;

// Probably best to use the unwrapped version of this below rather than use this directly.
const rawPlayersAtom = atomWithQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const gender = get(unwrappedGenderAtom);
  const queryKey = ['playerInfo', gender] as const;

  const queryFn = async ({ queryKey: [, gender] }: { queryKey: typeof queryKey }) => {
    const playerResponse = (await fetch(rawPlayersPath(gender))) as PlayerWithRels[];
    // don't await this, we don't need to wait for it to finish
    localforage.setItem(rawPlayersPath(gender), playerResponse);
    return playerResponse;
  };

  return { queryKey, queryFn, enabled: !!gender };
});

// Returns either the result of the rawPlayersAtom, or if that is pending falls back to the localforage cache.
export const rawPlayersWithCacheFallbackAtom = atom(async get => {
  const { data, isPending } = get(rawPlayersAtom);

  if (isPending) {
    const gender = get(unwrappedGenderAtom);
    const localForagePlayers = await localforage.getItem<PlayerWithRels[]>(rawPlayersPath(gender));
    return localForagePlayers || Promise.resolve([]);
  }

  return data ? Promise.resolve(data) : Promise.resolve([]);
});

export const isPopulatingPlayerCacheAtom = atom(
  async get => get(rawPlayersAtom).isPending && (await get(rawPlayersWithCacheFallbackAtom)).length === 0,
);

export const allPlayersOptionsAtom = atom(async get => {
  const players = await get(rawPlayersWithCacheFallbackAtom);

  return players.map(playerInfo => {
    const {
      player_id: playerId,
      nationality_area_code: nationalityAreaCode,
      name,
      nickname,
      current_team: currentTeam,
      date_of_birth: dateOfBirth,
      age,
    } = playerInfo;

    const clubName = currentTeam?.name ?? null;
    const yearOfBirth = dateOfBirth ? new Date(dateOfBirth).getFullYear() : null;
    const yearInfo = formatYearInfo(yearOfBirth, age);
    const context = [clubName, yearInfo].filter(Boolean) as string[];

    return convertToDropdownOption(formatPlayerName(nickname, name), playerId, {
      countryCode: nationalityAreaCode,
      context,
      nickname,
    });
  });
});

export const allPlayersSearchIndexAtom = atom(async get =>
  buildOptionSearchIndexAsync(await get(allPlayersOptionsAtom)),
);

export const unwrappedPlayersSearchIndexAtom = unwrap(allPlayersSearchIndexAtom);

export const playersAtom = atom(async get => {
  const rawPlayers = await get(rawPlayersWithCacheFallbackAtom);
  const rawCycles = (await get(rawCyclesAtom)).data;
  const selectedCompetitionIds = get(competitionIdsToFilterByAtom);
  const selectedSeasonsIds = get(seasonIdsToFilterByAtom);
  const selectedTeamIds = get(filtersAtom).teamIds;

  // filter the cycles by selected competitions and seasons to create an array of selected cycles
  const filteredCyclesByCompetition = filterCyclesBy(rawCycles, selectedCompetitionIds, 'competition_id');
  const selectedCycles = filterCyclesBy(filteredCyclesByCompetition, selectedSeasonsIds, 'season_id');
  const selectedCycleIds = selectedCycles.map(cycle => cycle.cycle_id);

  // filter players by selected teams and cycles
  const filteredPlayersByTeamsAndCycles =
    selectedTeamIds.length || selectedCycleIds.length
      ? rawPlayers.filter(player =>
          player.team_cycle_ids.some(
            teamCycleIds =>
              // ensure player played for selected team during selected seasons
              (selectedTeamIds.length ? selectedTeamIds.includes(teamCycleIds.team_id) : true) &&
              (selectedCycleIds.length ? isSharingId(teamCycleIds.cycle_ids, selectedCycleIds) : true),
          ),
        )
      : rawPlayers;

  return filteredPlayersByTeamsAndCycles;
});

export const playerOptionsAtom = atom(async get => {
  const players = await get(playersAtom);

  return players.map(playerInfo => {
    const { player_id: playerId, nationality_area_code: nationalityAreaCode, display_name: displayName } = playerInfo;
    return convertToDropdownOption(displayName, playerId, {
      countryCode: nationalityAreaCode,
    });
  });
});

const byLabel = ({ label: label1 }: StringOption, { label: label2 }: StringOption) => label1.localeCompare(label2);

export const playerNationalityOptionsAtom = atom(async get => {
  const players = await get(playersAtom);

  const optionsByAreaCode = players.reduce((optionsByAreaCode, player) => {
    const { nationality_area_code: code, nationality_area_name: name } = player;
    if (code && name && !optionsByAreaCode.has(code)) {
      optionsByAreaCode.set(code, convertToDropdownOption(name, code, { countryCode: code }));
    }
    return optionsByAreaCode;
  }, new Map<string, StringOption>());

  return [...optionsByAreaCode.values()].sort(byLabel);
});

export const unwrappedPlayerNationalityOptionsAtom = unwrap(playerNationalityOptionsAtom, prev => prev ?? []);

const rawIndividualPlayerInfoAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['individualPlayerInfo', get(playerIdAtom)] as const;

  const queryFn = async ({ queryKey: [_, playerId] }: { queryKey: typeof queryKey }) => {
    if (!playerId) return null;
    const playerResponse: Player[] = await fetch(`/players?eq[player_id]=${playerId}`);
    return playerResponse[0];
  };

  return { queryKey, queryFn };
});

/**
 * In order to avoid API calls, get the player info from the pre-fetched list of all players. (PlayerWithRels)
 * If the player is not in the list, fetch the player info from the API directly. (Player)
 *
 * A player might not be found if you try and view info for a player that is not in your selected competition gender.
 */
export const rawFilteredPlayerInfoAtom = atom(async get => {
  const selectedPlayerId = get(playerIdAtom);
  if (!selectedPlayerId) return null;
  const players = await get(rawPlayersWithCacheFallbackAtom);
  const player = players.find(({ player_id: playerId }) => playerId === selectedPlayerId);
  if (player) return player;

  const { data } = await get(rawIndividualPlayerInfoAtom);
  return data ?? null;
});

const rawPlayerGamesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['playerGames', get(playerIdAtom)] as const;

  const queryFn = async ({ queryKey: [_, playerId] }: { queryKey: typeof queryKey }) =>
    (await fetch(`/player/${playerId}/games`)) as Promise<PlayerGameWithRels[]>;

  return { queryKey, queryFn };
});

export const playerGamesAtom = atom(async get => {
  const playerGames = await get(rawPlayerGamesAtom);
  return Object.values(playerGames.data).map(objSnakeToCamel);
});

export const unwrappedPlayerGamesAtom = unwrap(playerGamesAtom, prev => prev ?? []);

const rawPlayerCyclesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['playerCycles', get(playerIdAtom)] as const;

  const queryFn = async ({ queryKey: [_, playerId] }: { queryKey: typeof queryKey }) =>
    (await fetch(`/player/${playerId}/cycles`)) as Promise<CycleWithRels[]>;
  return { queryKey, queryFn };
});

export const playerCyclesAtom = atom(async get => {
  const rawPlayerCycles = await get(rawPlayerCyclesAtom);
  return rawPlayerCycles.data.map(objSnakeToCamel);
});

export const playerCyclesOrderedAtom = atom(async get => {
  const playerCycles = await get(playerCyclesAtom);
  return sortCyclesBySeasonAndComp(playerCycles);
});

const filteredPlayerCyclesSeasonAtom = atom(async get => {
  const playerCycles = await get(playerCyclesAtom);
  const selectedCompetitionIds = get(competitionIdsToFilterByAtom);

  return filterCyclesWithRelsByCompetition(playerCycles, selectedCompetitionIds);
});

export const playerCompetitionOptionsAtom = createCompetitionOptionsAtom(playerCyclesAtom);

export const playerSeasonOptionsAtom = createSeasonOptionsAtom(filteredPlayerCyclesSeasonAtom);

const rawPlayerTeamsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['playerTeams', get(playerIdAtom)] as const;

  const queryFn = async ({ queryKey: [_, playerId] }: { queryKey: typeof queryKey }) =>
    (await fetch(`/player/${playerId}/teams`)) as Promise<TeamWithRels[]>;
  return { queryKey, queryFn };
});

export const playerTeamsAtom = atom(async get => {
  const playerCycles = (await get(rawPlayerCyclesAtom)).data;
  const rawPlayerTeams = (await get(rawPlayerTeamsAtom)).data;
  const selectedCompetitionIds = get(competitionIdsToFilterByAtom);
  const selectedSeasonsIds = get(seasonIdsToFilterByAtom);

  const cycles = playerCycles.map(({ cycle }) => cycle);

  // filter the cycles by selected competitions and seasons to create an array of "valid" cycles
  const filteredCyclesByCompetition = filterCyclesBy(cycles, selectedCompetitionIds, 'competition_id');
  const filteredCyclesBySeasons = filterCyclesBy(filteredCyclesByCompetition, selectedSeasonsIds, 'season_id');
  const validCycleIds = filteredCyclesBySeasons.map(cycle => cycle.cycle_id);

  // filter available teams by comparing the cycles they played in and the valid cycles ids
  const filteredTeamsByCycles =
    selectedCompetitionIds.length || selectedSeasonsIds.length
      ? rawPlayerTeams.filter(team => isSharingId(team.cycle_ids, validCycleIds))
      : rawPlayerTeams;

  return filteredTeamsByCycles;
});

// Unfiltered squad — all players who share any team with the focal player, regardless of
// competition/season selection. Used by the AI filter context so it always has the full
// teammate pool before the user has narrowed competitions.
export const allSquadPlayerOptionsAtom = atom(async get => {
  const [rawPlayers, { data: rawPlayerTeams }] = await Promise.all([
    get(rawPlayersWithCacheFallbackAtom),
    get(rawPlayerTeamsAtom),
  ]);
  const playerTeamIds = new Set(rawPlayerTeams.map(team => team.team_id));
  const focalPlayerId = get(playerIdAtom);

  return rawPlayers
    .filter(
      player =>
        player.player_id !== focalPlayerId &&
        player.team_cycle_ids.some(teamCycleIds => playerTeamIds.has(teamCycleIds.team_id)),
    )
    .map(({ player_id: value, display_name: label }) => ({ label, value: String(value) }));
});

export const squadPlayerOptionsAtom = atom(async get => {
  const [players, playerTeams] = await Promise.all([get(playersAtom), get(playerTeamsAtom)]);
  const playerTeamIds = new Set(playerTeams.map(team => team.team_id));
  const focalPlayerId = get(playerIdAtom);

  return players
    .filter(
      player =>
        player.player_id !== focalPlayerId &&
        player.team_cycle_ids.some(teamCycleIds => playerTeamIds.has(teamCycleIds.team_id)),
    )
    .map(({ player_id: value, display_name: label }) => ({ label, value: String(value) }));
});

export const unwrappedSquadPlayerOptionsAtom = unwrap(squadPlayerOptionsAtom, prev => prev || []);

export const playerTeamsOptionsAtom = atom(async get => {
  const playerTeams = await get(playerTeamsAtom);

  return playerTeams
    .map(objSnakeToCamel)
    .map(({ name, teamId, areaCode }) => convertToDropdownOption(name, teamId, { countryCode: areaCode }));
});

export const unwrappedPlayerTeamsOptionsAtom = unwrap(playerTeamsOptionsAtom, prev => prev || []);

const rawPlayerIdMappingAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['playerIdMapping'] as const;

  const queryFn = async () => (await fetch('/players/mapping')) as Promise<Mapping[]>;
  return { queryKey, queryFn };
});

export const playerIdMappingAtom = atom(async get => datahubToIqIdMapping((await get(rawPlayerIdMappingAtom)).data));

export const unwrappedPlayerIdMappingAtom = unwrap(playerIdMappingAtom, prev => prev || {});

const rawPlayerNavigationLinkIdsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['playerNavigationLinkIds', get(globalPlayerIdAtom), get(hasUserSelectedPlayerAtom)] as const;

  const queryFn = async ({ queryKey: [_, playerId, hasUserSelectedPlayer] }: { queryKey: typeof queryKey }) => {
    const noResultResponse = {
      /* this is following a pattern to just have the links use 0 id */
      team: { datahub_id: 0, iq_id: 0 },
      game: { datahub_id: 0, iq_id: 0 },
      competition: { datahub_id: 0, iq_id: 0 },
      keeper: { datahub_id: 0, iq_id: 0 },
    };

    if (!playerId || !hasUserSelectedPlayer) return noResultResponse;

    try {
      const result = await fetch(`/player/${playerId}/navigation-link-ids`);
      return result as PlayerNavigationLinkIds;
    } catch (err: any) {
      // This might not be perfect, but at least means that if this endpoint errors for any reason the
      // nav bar still renders (albeit with links which take you to a blank page)
      // eslint-disable-next-line no-console
      console.warn(`Failed to fetch navigation links for player ${playerId}:`, err);
      return noResultResponse;
    }
  };
  return { queryKey, queryFn };
});

const playerNavigationLinkIdsAtom = atom<Promise<PlayerNavigationLinkIds>>(
  async get => (await get(rawPlayerNavigationLinkIdsAtom)).data,
);

export const playerNavigationTeamLinkIdAtom = atom(async get =>
  objSnakeToCamel((await get(playerNavigationLinkIdsAtom)).team),
);

export const playerNavigationGameLinkIdAtom = atom(async get =>
  objSnakeToCamel((await get(playerNavigationLinkIdsAtom)).game),
);

export const playerNavigationCompetitionLinkIdAtom = atom(async get =>
  objSnakeToCamel((await get(playerNavigationLinkIdsAtom)).competition),
);

export const playerNavigationKeeperLinkIdAtom = atom(async get =>
  objSnakeToCamel((await get(playerNavigationLinkIdsAtom)).keeper),
);

const distributionPositionGroupAtom = atom(
  async get => get(radarTemplatePositionGroupAtom) ?? get(selectedPlayerTemplatePositionGroupAtom),
);

const unwrappedDistributionPositionGroupAtom = unwrap(distributionPositionGroupAtom, prev => prev);

const rawPlayerMetricDistributionPercentilesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'playerMetricDistributionPercentiles',
    get(unwrappedGenderAtom),
    get(unwrappedDistributionPositionGroupAtom),
    get(metricsAtom),
  ] as const;

  const queryFn = async ({ queryKey: [, gender, positionGroup, metrics] }: { queryKey: typeof queryKey }) => {
    if (!gender || !positionGroup || !metrics.length) return null;

    const params = {
      eq: { gender, positionGroup },
      in: { metrics },
    };
    const url = metricDistributionPercentilesUrl('players', params);
    const data: MetricDistributionPercentiles[] = await fetch(url);
    return data;
  };

  return { queryKey, queryFn };
});

export const playerMetricDistributionPercentilesAtom = atom(async get => {
  const rawPercentiles = await get(rawPlayerMetricDistributionPercentilesAtom);
  return formatMetricDistributions(rawPercentiles);
});

const rawPlayerMetricDistributionBucketsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'playerMetricDistributionBuckets',
    get(unwrappedGenderAtom),
    get(unwrappedDistributionPositionGroupAtom),
    get(metricsAtom),
  ] as const;
  const queryFn = async ({ queryKey: [, gender, positionGroup, metrics] }: { queryKey: typeof queryKey }) => {
    if (!gender || !positionGroup || !metrics.length) return null;

    const params = {
      eq: { gender, positionGroup },
      in: { metrics },
    };
    const url = metricDistributionBucketsUrl('players', params);

    const data: MetricDistributionBucket[] = await fetch(url);

    return data;
  };

  return { queryKey, queryFn };
});

const playerMetricDistributionBucketsAtom = atom(async get =>
  formatMetricDistributionBuckets(await get(rawPlayerMetricDistributionBucketsAtom)),
);

/** 'All positions' fallback atoms - used when position-specific distributions are missing */
const rawPlayerMetricDistributionPercentilesAllPositionsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'playerMetricDistributionPercentilesAllPositions',
    get(unwrappedGenderAtom),
    get(metricsAtom),
  ] as const;

  const queryFn = async ({ queryKey: [, gender, metrics] }: { queryKey: typeof queryKey }) => {
    if (!gender || !metrics.length) return null;

    const params = {
      eq: { gender, positionGroup: 'all' },
      in: { metrics },
    };
    const url = metricDistributionPercentilesUrl('players', params);
    const data: MetricDistributionPercentiles[] = await fetch(url);
    return data;
  };

  return { queryKey, queryFn };
});

const playerMetricDistributionPercentilesAllPositionsAtom = atom(async get => {
  const rawPercentiles = await get(rawPlayerMetricDistributionPercentilesAllPositionsAtom);
  return formatMetricDistributions(rawPercentiles);
});

// Selector atoms that merge position-specific and all-positions data with fallback logic
const playerMetricDistributionPercentilesWithFallbackAtom = atom(async get => {
  const [positionSpecific, allPositions] = await Promise.all([
    get(playerMetricDistributionPercentilesAtom),
    get(playerMetricDistributionPercentilesAllPositionsAtom),
  ]);
  const metrics = get(metricsAtom);

  return metrics.map(metricKey => getMetricDistribution(metricKey, positionSpecific, allPositions));
});

const playerMetricDistributionBucketsWithFallbackAtom = atom(async get => {
  const [positionSpecific, allPositionsPercentiles] = await Promise.all([
    get(playerMetricDistributionBucketsAtom),
    get(playerMetricDistributionPercentilesAllPositionsAtom),
  ]);
  const metrics = get(metricsAtom);

  return metrics.map(metricKey => getMetricBucket(metricKey, positionSpecific, allPositionsPercentiles));
});

/** Fetch percentiles and buckets for selected metrics in parallel. This eliminates sequential suspension points. */
export const playerMetricPercentilesAndBucketsAtom = atom(async get => {
  const [percentiles, buckets] = await Promise.all([
    get(playerMetricDistributionPercentilesWithFallbackAtom),
    get(playerMetricDistributionBucketsWithFallbackAtom),
  ]);

  return { percentiles, buckets };
});
